import React, { useState } from "react"
import clsx from "clsx"
import { useSelector, useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import {
    AppBar,
    Toolbar,
    Typography,
    Drawer,
    List,
    IconButton,
    ListItem,
    ListItemText,
    Divider,
    Tooltip,
    ListItemIcon,
    Chip,
    Box
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import Brightness3Icon from "@mui/icons-material/Brightness3"
import WbSunnyIcon from "@mui/icons-material/WbSunny"
import HomeIcon from "@mui/icons-material/Home"
import DashboardIcon from "@mui/icons-material/Dashboard"
import WebIcon from "@mui/icons-material/Web"
import LogoutIcon from "@mui/icons-material/Logout"
import PersonIcon from "@mui/icons-material/Person"
import NewReleasesIcon from "@mui/icons-material/NewReleases"
import firebaseAuth from "../../auth/firebaseAuth"
import logo from "../../images/precipartLogoSquare.png"
import { AppBarShiftStyles, AppBarStyles, ApplicationNameStyles, ContentShiftStyles, ContentStyles, DrawerHeaderStyles, DrawerStyles, LogoStyles, chipStyles } from "./styles/NavigationStyles"
import {
    getUserData,
    toggleDarkMode,
    logoutUser
} from "../../actions/authActions"
import { homeURL, environment } from "../../config"

function Navigation() {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const userData = useSelector((state) => state.auth.userData)
    const permissions = useSelector((state) => state.auth.permissions)

    const [open, setOpen] = useState(false)

    const navigationItems = [
        {
            text: "Home",
            path: "/home",
            visible: true,
            icon: <DashboardIcon />
        },
        {
            text: "Applications",
            path: "/applications",
            visible: permissions.ViewApplications,
            icon: <WebIcon />
        },
        {
            text: "User Access",
            path: "/user-access",
            visible: permissions.ViewUserAccess,
            icon: <PersonIcon />
        }
    ]

    const handleDarkModeToggle = async () => {
        await dispatch(toggleDarkMode(userData))
        await dispatch(getUserData(userData.GoogleID))
    }

    const handleDrawerOpen = () => {
        setOpen(true)
    }

    const handleDrawerClose = () => {
        setOpen(false)
    }

    const handleGoHome = async () => {
        window.open(homeURL, "_self")
        setOpen(false)
    }

    const handleLogout = async () => {
        handleDrawerClose()
        dispatch(logoutUser())
        await firebaseAuth.signOut()
        navigate("/")
    }
    
    const handleKeyDown = async (e) => {
        if (e.key === "Escape") {
            setOpen(false)
        }
    }

    return (
        <div style={{ display: "flex" }}>
            <AppBar
                position="fixed"
                sx={[
                    AppBarStyles,
                    open && AppBarShiftStyles,
                ]}
                elevation={24}
                onKeyDown={handleKeyDown}
            >
                <Toolbar>
                    <Box
                        component="img"
                        alt="Precipart Logo"
                        src={logo}
                        sx={LogoStyles}
                    />
                    <Typography
                        variant="h6"
                        sx={ApplicationNameStyles}
                        noWrap
                    >
                        Application Administration{" "}
                        {environment && environment !== "production" && (
                            <Chip
                                icon={<NewReleasesIcon />}
                                label={environment.toUpperCase()}
                                style={{ marginLeft: "5px" }}
                            />
                        )}
                    </Typography>
                    {userData.AssociateOID && (
                        <Tooltip title={open ? "Close Menu" : "Open Menu"}>
                            <IconButton
                                color="inherit"
                                aria-label={
                                    open ? "close drawer" : "open drawer"
                                }
                                edge="end"
                                onClick={
                                    open ? handleDrawerClose : handleDrawerOpen
                                }
                            >
                                <MenuIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Toolbar>
            </AppBar>

            <Box
                sx={[
                    ContentStyles,
                    open && ContentShiftStyles,
                ]}
            >
                <Box sx={DrawerHeaderStyles} />
            </Box>
            <Drawer
                sx={DrawerStyles}
                variant="persistent"
                anchor="right"
                open={open}
                PaperProps={{ elevation: 15 }}
            >
                <Box sx={DrawerHeaderStyles}></Box>
                <Box sx={DrawerHeaderStyles}>
                    <Tooltip title="Return Home">
                        <IconButton>
                            <HomeIcon onClick={handleGoHome} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Logout">
                        <IconButton>
                            <LogoutIcon onClick={handleLogout} />
                        </IconButton>
                    </Tooltip>
                    {userData && (
                        <Tooltip title="Toggle Darkmode">
                            <IconButton onClick={handleDarkModeToggle}>
                                {userData.DarkMode === "dark" ? (
                                    <WbSunnyIcon />
                                ) : (
                                    <Brightness3Icon />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Divider />
                <List>
                    {navigationItems
                        .filter((item) => item.visible)
                        .map((item) => (
                            <ListItem
                                button
                                key={item.text}
                                component={Link}
                                to={item.path}
                                onClick={handleDrawerClose}
                                sx={{
                                    textDecoration: "none",
                                    color: "inherit",
                                }}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItem>
                        ))}
                </List>
            </Drawer>
        </div>
    )
}

export default Navigation
